import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Search, Plus, Pencil, Trash2, Eye, Loader2, Mail, Phone, MapPin, Briefcase, Building2, Upload } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", email: "", phone: "", department: "", designation: "",
  employment_type: "Full-time", status: "active", salary: 0, location: "",
  join_date: "", gender: "", date_of_birth: "", address: "", avatar: "",
  father_name: "", pran: "", local_address: "", permanent_address: "",
  bank_account_holder: "", bank_account_number: "", bank_name: "", ifsc: "",
  create_login: false, password: "", account_role: "employee",
};

const StatusBadge = ({ status }) => {
  const map = {
    active: "bg-[#2f7d5b]/12 text-[#2f7d5b]",
    on_leave: "bg-accent/15 text-accent",
    inactive: "bg-muted text-muted-foreground",
  };
  const label = { active: "Active", on_leave: "On Leave", inactive: "Inactive" }[status] || status;
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[status] || "bg-muted"}`}>{label}</span>;
};

export default function Directory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [depts, setDepts] = useState([]);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [viewEmp, setViewEmp] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handlePhotoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, avatar: data.path }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (deptFilter !== "all") params.department = deptFilter;
    api.get("/employees", { params }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { api.get("/departments").then((r) => setDepts(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, deptFilter]);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setDialogOpen(true); };
  const openEdit = (e) => {
    setForm({ ...EMPTY, ...e });
    setEditId(e.id);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, salary: Number(form.salary) || 0 };
      if (editId) {
        const { data } = await api.put(`/employees/${editId}`, payload);
        toast.success(data.account_created ? "Employee updated & login account created" : "Employee updated");
      } else {
        const { data } = await api.post("/employees", payload);
        toast.success(data.account_created ? "Employee added & login account created" : "Employee added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/employees/${deleteId}`);
      toast.success("Employee removed");
      setDeleteId(null);
      load();
    } catch (err) { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="directory-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground ashoka-underline inline-block">Employee Directory</h2>
          <p className="text-muted-foreground mt-3">{rows.length} records found</p>
        </div>
        {isAdmin && (
          <Button data-testid="add-employee-button" onClick={openAdd} className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Plus className="w-4 h-4 mr-1.5" /> Add Employee
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="search-employee-input" placeholder="Search by name, email, code, designation..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="department-filter-select"><SelectValue placeholder="All Departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {depts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">No employees found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50 text-left">
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Employee</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Code</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden md:table-cell">Department</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden lg:table-cell">Designation</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} data-testid={`employee-row-${e.id}`} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold shrink-0 overflow-hidden">
                          {e.avatar ? <img src={resolveAvatar(e.avatar)} alt={e.name} className="w-full h-full object-cover" /> : e.name?.[0]}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-foreground truncate">{e.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{e.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{e.employee_code}</td>
                    <td className="px-4 py-3 hidden md:table-cell">{e.department || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell">{e.designation || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`view-profile-button-${e.id}`} onClick={() => setViewEmp(e)} className="p-2 rounded-md hover:bg-secondary text-muted-foreground hover:text-primary transition-colors" title="View">
                          <Eye className="w-4 h-4" />
                        </button>
                        {isAdmin && (
                          <>
                            <button data-testid={`edit-employee-button-${e.id}`} onClick={() => openEdit(e)} className="p-2 rounded-md hover:bg-secondary text-muted-foreground hover:text-primary transition-colors" title="Edit">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button data-testid={`delete-employee-button-${e.id}`} onClick={() => setDeleteId(e.id)} className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editId ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <Field label="Full Name *"><Input data-testid="emp-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Email *"><Input data-testid="emp-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Department">
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger data-testid="emp-department-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{depts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Designation"><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></Field>
            <Field label="Employment Type">
              <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Full-time">Full-time</SelectItem>
                  <SelectItem value="Contract">Contract</SelectItem>
                  <SelectItem value="Part-time">Part-time</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Salary (₹/month)"><Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} /></Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="emp-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
            <Field label="Join Date"><Input type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} /></Field>
            <Field label="Gender">
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date of Birth"><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></Field>
            <div className="sm:col-span-2">
              <Field label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            </div>
            <Field label="Father's Name"><Input value={form.father_name} onChange={(e) => setForm({ ...form, father_name: e.target.value })} /></Field>
            <Field label="PRAN"><Input value={form.pran} onChange={(e) => setForm({ ...form, pran: e.target.value })} /></Field>
            <div className="sm:col-span-2"><Field label="Local Address"><Input value={form.local_address} onChange={(e) => setForm({ ...form, local_address: e.target.value })} /></Field></div>
            <div className="sm:col-span-2"><Field label="Permanent Address"><Input value={form.permanent_address} onChange={(e) => setForm({ ...form, permanent_address: e.target.value })} /></Field></div>
            <Field label="Bank A/C Holder"><Input value={form.bank_account_holder} onChange={(e) => setForm({ ...form, bank_account_holder: e.target.value })} /></Field>
            <Field label="Bank Account No."><Input value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} /></Field>
            <Field label="Bank Name"><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></Field>
            <Field label="IFSC Code"><Input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} /></Field>
            <div className="sm:col-span-2">
              <Field label="Employee Photo">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-bold overflow-hidden shrink-0 border border-border">
                    {form.avatar ? <img src={resolveAvatar(form.avatar)} alt="preview" className="w-full h-full object-cover" /> : (form.name?.[0] || "?")}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="emp-photo-file-input" />
                    <Button type="button" variant="outline" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid="emp-photo-upload-button" className="w-full sm:w-auto">
                      {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-1.5" /> Upload from device</>}
                    </Button>
                    <Input data-testid="emp-avatar-input" placeholder="or paste an image URL" value={form.avatar} onChange={(e) => setForm({ ...form, avatar: e.target.value })} />
                  </div>
                </div>
              </Field>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-secondary/40 p-4 space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox data-testid="create-login-checkbox" checked={form.create_login} onCheckedChange={(v) => setForm({ ...form, create_login: !!v })} />
              <span className="text-sm font-semibold text-foreground">Create a login account for this employee</span>
            </label>
            {form.create_login && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Login Password *">
                  <Input data-testid="emp-login-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </Field>
                <Field label="Account Role">
                  <Select value={form.account_role} onValueChange={(v) => setForm({ ...form, account_role: v })}>
                    <SelectTrigger data-testid="emp-account-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <p className="sm:col-span-2 text-xs text-muted-foreground">
                  The person will sign in using their email ({form.email || "email above"}) and this password.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-employee-button" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editId ? "Update" : "Add Employee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View profile sheet */}
      <Sheet open={!!viewEmp} onOpenChange={(o) => !o && setViewEmp(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="employee-profile-sheet">
          {viewEmp && (
            <>
              <SheetHeader><SheetTitle className="font-heading">Employee Profile</SheetTitle></SheetHeader>
              <div className="mt-6 flex flex-col items-center text-center">
                <div className="w-24 h-24 rounded-full bg-primary/10 text-primary flex items-center justify-center text-3xl font-bold overflow-hidden">
                  {viewEmp.avatar ? <img src={resolveAvatar(viewEmp.avatar)} alt={viewEmp.name} className="w-full h-full object-cover" /> : viewEmp.name?.[0]}
                </div>
                <h3 className="font-heading text-xl font-bold mt-4">{viewEmp.name}</h3>
                <p className="text-muted-foreground">{viewEmp.designation}</p>
                <div className="mt-2"><StatusBadge status={viewEmp.status} /></div>
              </div>
              <div className="mt-6 space-y-3 text-sm">
                <Info icon={Briefcase} label="Employee Code" value={viewEmp.employee_code} mono />
                <Info icon={Building2} label="Department" value={viewEmp.department} />
                <Info icon={Mail} label="Email" value={viewEmp.email} />
                <Info icon={Phone} label="Phone" value={viewEmp.phone} />
                <Info icon={MapPin} label="Location" value={viewEmp.location} />
                <Info icon={Briefcase} label="Employment" value={viewEmp.employment_type} />
                <Info icon={Briefcase} label="Salary" value={viewEmp.salary ? `₹${Number(viewEmp.salary).toLocaleString("en-IN")}/mo` : "—"} mono />
                <Info icon={Briefcase} label="Joined" value={viewEmp.join_date} />
                <Info icon={Briefcase} label="Gender" value={viewEmp.gender} />
                {viewEmp.address && <Info icon={MapPin} label="Address" value={viewEmp.address} />}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove employee?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete the employee record. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={doDelete} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
    {children}
  </div>
);

const Info = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-start gap-3 py-2 border-b border-border last:border-0">
    <Icon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-foreground ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  </div>
);
